import { useEffect, useState } from "react";
import { ZoomIn, ZoomOut, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;
const STEP = 0.25;

// Fullscreen image viewer with zoom in/out controls.
// Wheel/pinch and drag are handled by CSS transform on the image element.
export const PhotoZoomDialog = ({ open, onOpenChange, src, alt }) => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (open) setScale(1);
  }, [open]);

  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, +(s + STEP).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(MIN_SCALE, +(s - STEP).toFixed(2)));
  const reset = () => setScale(1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[92vw] h-[85vh] p-0 bg-white flex flex-col"
        data-testid="photo-zoom-dialog"
      >
        <DialogTitle className="sr-only">{alt || "Photo preview"}</DialogTitle>
        <div className="flex-1 overflow-auto flex items-center justify-center bg-secondary/30">
          {src ? (
            <img
              src={src}
              alt={alt || "preview"}
              style={{ transform: `scale(${scale})`, transformOrigin: "center center", transition: "transform 120ms ease" }}
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
              data-testid="photo-zoom-image"
            />
          ) : null}
        </div>
        <div className="border-t border-border p-2 flex items-center justify-center gap-2 bg-card">
          <Button variant="outline" size="sm" onClick={zoomOut} disabled={scale <= MIN_SCALE} data-testid="photo-zoom-out">
            <ZoomOut size={16} />
          </Button>
          <button
            type="button"
            onClick={reset}
            className="font-mono text-xs w-16 h-8 rounded-md border border-border hover:bg-secondary transition-colors"
            data-testid="photo-zoom-reset"
            title="Reset to 100%"
          >
            {Math.round(scale * 100)}%
          </button>
          <Button variant="outline" size="sm" onClick={zoomIn} disabled={scale >= MAX_SCALE} data-testid="photo-zoom-in">
            <ZoomIn size={16} />
          </Button>
          <div className="mx-2 w-px h-6 bg-border" />
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="photo-zoom-close">
            <X size={16} />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
